import React, { useEffect, useState } from "react";
import { Card, Table, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import { getUserId } from "../services/authorize";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

export default function IslamicAttendancePage() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [yearId, setYearId]     = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList] = useState([]);
  const [data, setData]         = useState([]);

  useEffect(() => {
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`)
      .then(res => {
        const list = res.data;
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) { id = String(list[0].year_id); localStorage.setItem("yearId", id); }
        setYearId(id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/mySubjectsOnDay/${getUserId()}/${yearId}`)
      .then(res => setData(res.data))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [yearId, setLoading]);

  function getPeriodOrder(row) { return row.period_order; }

  function groupConsecutivePeriods(rows) {
    const sorted = [...rows].sort((a, b) =>
      a.is_teachId - b.is_teachId ||
      a.ir_id - b.ir_id ||
      getPeriodOrder(a) - getPeriodOrder(b)
    );
    const result = [];
    let group = [];
    for (let i = 0; i < sorted.length; i++) {
      const curr = sorted[i];
      const prev = sorted[i - 1];
      if (
        i === 0 ||
        curr.is_teachId !== prev.is_teachId ||
        curr.ir_id !== prev.ir_id ||
        getPeriodOrder(curr) !== getPeriodOrder(prev) + 1
      ) {
        if (group.length) result.push(group);
        group = [curr];
      } else {
        group.push(curr);
      }
    }
    if (group.length) result.push(group);
    return result;
  }

  const fmt = (time) => time ? String(time).substring(0, 5) : '';
  const grouped = groupConsecutivePeriods(data);
  const today = new Date().getDay();
  const genderLabel = (g) => g === 'm' ? t.common.male : t.common.female;

  const renderWeekSchedule = (slot_summary) => {
    if (!slot_summary) return null;
    const groups = {};
    slot_summary.split(',').forEach(pair => {
      const [day, order, begin, end] = pair.split('|');
      const d = Number(day);
      if (!groups[d]) groups[d] = [];
      groups[d].push({ order: Number(order), begin, end });
    });
    return (
      <div className="d-flex flex-wrap gap-1 mt-1">
        {Object.entries(groups).map(([day, items]) => (
          <span key={day} className={`badge small fw-normal ${Number(day) === today ? 'bg-info' : 'bg-light text-dark border'}`}>
            {t.days[Number(day)]} {items.map(s => s.order).join(',')}
          </span>
        ))}
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="main-title mb-0">{t.attendance.pageTitle}</h4>
          <p className="text-secondary mb-0 small">{t.days[today]} — {new Date().toLocaleDateString('en-GB')}</p>
        </div>
        <select
          className="form-select w-auto"
          value={yearId || ""}
          onChange={e => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }}
        >
          {yearList.map(y => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYearShort(y.year_year)}</option>
          ))}
        </select>
      </div>

      <Card className="card-one">
        <Card.Body className="p-0">
          {grouped.length === 0 ? (
            <p className="text-center text-secondary py-5">{t.attendance.noSchedule}</p>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 70 }}>{t.attendance.colPeriod}</th>
                  <th style={{ width: 110 }}>{t.attendance.colTime}</th>
                  <th>{t.attendance.colSubject}</th>
                  <th style={{ width: 100 }}>{t.attendance.colClass}</th>
                  <th style={{ width: 110 }}>{t.attendance.colGender}</th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {grouped
                  .sort((a, b) => a[0].period_order - b[0].period_order)
                  .map(group => {
                    const first = group[0];
                    const last  = group[group.length - 1];
                    const periodText = group.length > 1
                      ? `${first.period_order} – ${last.period_order}`
                      : `${first.period_order}`;
                    const timeText = group.length > 1
                      ? `${fmt(first.period_begin)} – ${fmt(last.period_end)}`
                      : `${fmt(first.period_begin)} – ${fmt(first.period_end)}`;
                    return (
                      <tr key={`${first.is_teachId}-${first.period_order}`}>
                        <td className="align-middle fw-semibold">{periodText}</td>
                        <td className="align-middle text-secondary small">{timeText}</td>
                        <td className="align-middle arabic-text">
                          <span className="fw-semibold">{first.isubject_isid}</span>
                          <span className="ms-2 text-secondary">{first.isubject_name}</span>
                          {renderWeekSchedule(first.slot_summary)}
                        </td>
                        <td className="align-middle">
                          {first.ic_class}/{first.ir_no}
                        </td>
                        <td className="align-middle">
                          <Badge bg={first.ir_gender === 'm' ? 'info' : 'danger'} className="fw-normal">
                            {genderLabel(first.ir_gender)}
                          </Badge>
                        </td>
                        <td className="align-middle text-center">
                          <Link
                            to={`/islamic/check-attendance/${first.ir_id}/${first.is_teachId}`}
                            state={{ periods: group.map(g => g.period_id) }}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="ri-user-follow-line me-1"></i>{t.attendance.checkBtn}
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </React.Fragment>
  );
}
